import { memo, useCallback, useState } from 'react';

import { WorkspaceAgentFieldsControl } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/form/WorkspaceAgentFieldsControl';
import { WorkspaceAgentPanelConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDock';
import { PrivateWorkspaceAgentMonikerText } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/read/PrivateWorkspaceAgentMonikerText';
import { FIELD_VALUE_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentFormInput';
import { useTrackWorkspaceAgentFormChoice } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useTrackWorkspaceAgentFormChoice';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type TextFieldsElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type PrivateWorkspaceAgentPanelFieldsDockProps = {
  elicitation: TextFieldsElicitation;
};

export const PrivateWorkspaceAgentPanelFieldsDock = memo(
  ({ elicitation }: PrivateWorkspaceAgentPanelFieldsDockProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const trackChoice = useTrackWorkspaceAgentFormChoice(elicitation, {
      fieldCount: elicitation.payload.fields.length,
    });
    const [values, setValues] = useState<Record<string, string>>({});
    const canSubmit = elicitation.payload.fields.every((field) => {
      const value = (values[field.label] ?? '').trim();

      return value.length > 0 && value.length <= FIELD_VALUE_MAX_LENGTH;
    });

    const handleChange = useCallback((label: string, value: string) => {
      setValues((current) => ({ ...current, [label]: value }));
    }, []);

    const handleContinue = useCallback(() => {
      if (!canSubmit) {
        return;
      }

      trackChoice({ cta: 'approve' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        fieldValues: elicitation.payload.fields.map((field) => ({
          label: field.label,
          value: (values[field.label] ?? '').trim(),
        })),
        response: 'approved',
      });
    }, [canSubmit, elicitation.id, elicitation.payload.fields, session, trackChoice, values]);

    const handleCancel = useCallback(() => {
      trackChoice({ cta: 'cancel' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'denied',
      });
    }, [elicitation.id, session, trackChoice]);

    return (
      <WorkspaceAgentPanelConsentDock
        title={<PrivateWorkspaceAgentMonikerText value={elicitation.payload.prompt} />}
        approveLabel="Continue"
        data-testid="WORKSPACE_AGENT_FIELDS"
        isApproveDisabled={!canSubmit}
        onApprove={handleContinue}
        onCancel={handleCancel}
      >
        <WorkspaceAgentFieldsControl fields={elicitation.payload.fields} onChange={handleChange} values={values} />
      </WorkspaceAgentPanelConsentDock>
    );
  },
);

PrivateWorkspaceAgentPanelFieldsDock.displayName = 'PrivateWorkspaceAgentPanelFieldsDock';
